import { useLayoutEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useEmbedContext } from './embed-context';
var PageContexts;
(function (PageContexts) {
    PageContexts["ANSWER"] = "answer";
    PageContexts["LIVEBOARD"] = "liveboard";
    PageContexts["SEARCH"] = "search-answer";
    PageContexts["SPOTTER"] = "spotter";
    PageContexts["OTHER"] = "other";
})(PageContexts || (PageContexts = {}));
var PageType;
(function (PageType) {
    PageType["PAGE"] = "page";
    PageType["DIALOG"] = "dialog";
})(PageType || (PageType = {}));
const defaultPageContextOptions = {
    page: PageContexts.OTHER,
    pageType: PageType.PAGE,
    objectIds: {},
};
let pageContextStack = [{
        ...defaultPageContextOptions,
        _timestamp: 0,
    }];
const timestampCache = new Map();
const getContextKey = (ctx) => {
    return JSON.stringify({
        page: ctx.page,
        pageType: ctx.pageType,
        objectIds: ctx.objectIds,
    });
};
const buildNestedNavigationContext = (stack) => {
    if (!stack.length) {
        return {
            stack: [],
            currentContext: {
                name: defaultPageContextOptions.page,
                type: defaultPageContextOptions.pageType,
                objectIds: defaultPageContextOptions.objectIds,
            },
        };
    }
    const effectiveStack = stack.length > 1 && stack[0].page === PageContexts.OTHER
        ? stack.slice(1)
        : stack;
    const contextStack = effectiveStack.map(ctx => ({
        name: ctx.page,
        type: ctx.pageType,
        objectIds: ctx.objectIds,
    }));
    const currentContext = contextStack.length > 0
        ? contextStack[contextStack.length - 1]
        : {
            name: defaultPageContextOptions.page,
            type: defaultPageContextOptions.pageType,
            objectIds: defaultPageContextOptions.objectIds,
        };
    return {
        stack: contextStack,
        currentContext,
    };
};
const getPageContext = () => {
    return pageContextStack[pageContextStack.length - 1] || defaultPageContextOptions;
};
const getNavigationContext = () => {
    return buildNestedNavigationContext(pageContextStack);
};
const setPageContext = (partial) => {
    const currentContext = getPageContext();
    if (isEqual(partial, currentContext)) {
        return () => { };
    }
    const mergedContext = {
        ...currentContext,
        ...partial,
    };
    const contextKey = getContextKey(mergedContext);
    let timestamp = timestampCache.get(contextKey);
    if (timestamp === undefined) {
        timestamp = Date.now();
        timestampCache.set(contextKey, timestamp);
    }
    const newEntry = {
        ...mergedContext,
        _timestamp: timestamp,
    };
    pageContextStack = pageContextStack.filter(e => getContextKey(e) !== contextKey);
    pageContextStack = [...pageContextStack, newEntry];
    pageContextStack.sort((a, b) => a._timestamp - b._timestamp);
    const reset = () => {
        pageContextStack = pageContextStack.filter(e => e !== newEntry);
        setTimeout(() => {
            const stillInStack = pageContextStack.some(e => getContextKey(e) === contextKey);
            if (!stillInStack) {
                timestampCache.delete(contextKey);
            }
        }, 100);
        if (pageContextStack.length === 0) {
            pageContextStack = [{
                    ...defaultPageContextOptions,
                    _timestamp: 0,
                }];
        }
    };
    return reset;
};
const useSetPageContext = (pageContext) => {
    const { isEmbedded } = useEmbedContext();
    const context = useMemo(() => pageContext, [pageContext]);
    useLayoutEffect(() => {
        if (!isEmbedded) {
            return () => { };
        }
        const reset = setPageContext(context);
        return () => reset();
    }, [isEmbedded, context]);
};
export { getPageContext, setPageContext, useSetPageContext, PageContexts, PageType, getNavigationContext };
//# sourceMappingURL=page-context.js.map